import React, { useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";
import { UserRoleStrings } from "modules/authorization/models";
import { IMenuItem, MenuItem, MenuItemType, MenuItemLabel } from "../../common/components/MenuItem";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import { Theme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import BuildIcon from "@mui/icons-material/Build";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PostAddIcon from '@mui/icons-material/PostAdd';
import { MenuUtils } from "routes/_layout/common/menuUtils";
import LinkIcon from "@mui/icons-material/Link";
import { ShortcutGroupToShortcuts } from "modules/shortcuts/models";
import { useMediaQuery, useTheme } from "@mui/material";

const useStyles = makeStyles<
    Theme, 
    { 
        drawerExpanded: boolean, 
        drawerWidth: number, 
        expandedDrawerWidth: number
    }
>((theme: Theme) =>
    createStyles({
        list: {
            position: "static"
        },
        poweredBy: {
            color: "#aaaaaa",
            fontSize: 10,
            marginBottom: 10,
            marginLeft: 23,
            marginTop: 10,
            opacity: 0,
            "&.expanded": {
                opacity: 1,
                transition: "opacity linear 0.1s 0.2s"
            }
        }
    })
);

const MenuItems = (
    props: PropsWithRedux
): IMenuItem[] => {
    let items: IMenuItem[] = [];

    if (props.portalPagesEnabled && !!props.navigation.navigationItems.length) {
        items = [
            { label: MenuItemLabel.HOME, icon: <HomeOutlinedIcon />, type: MenuItemType.Link, url: "~/home" }
        ];

        if (props.portalPagesEnabled && props.navigation.navigationItems.length > 1) {
            items = items.concat([
                {
                    label: MenuItemLabel.PORTAL_PAGES, icon: <MenuBookIcon />, type: MenuItemType.Link, children: props.navigation.navigationItems.slice(1).map((navigationItem) => (
                        {
                            label: navigationItem.name, type: MenuItemType.Link, url: `~/page/${navigationItem.url}`, children: navigationItem.children.map((childNavigationItem) => (
                                { label: childNavigationItem.name, type: MenuItemType.Link, url: `~/page/${childNavigationItem.url}` }
                            ))
                        }
                    ))
                }
            ]);
        }

        items = items.concat([
            { label: MenuItemLabel.EMPTY, type: MenuItemType.Divider },
            { label: MenuItemLabel.FEED, icon: <LibraryBooksIcon />, type: MenuItemType.Link, url: "~/posts" }
        ]);
    } else {
        items = [
            { label: MenuItemLabel.FEED, icon: <LibraryBooksIcon />, type: MenuItemType.Link, url: "~/home" }
        ];
    }

    items = items.concat([
        { label: MenuItemLabel.EVENTS, icon: <EventOutlinedIcon />, type: MenuItemType.Link, url: "~/events", hide: !props.eventsEnabled },
        { label: MenuItemLabel.DOCUMENTS, icon: <FolderOutlinedIcon />, type: MenuItemType.Link, url: "~/documents", hide: !props.documentsEnabled },
        { 
            label: MenuItemLabel.SHORTCUTS, 
            icon: <LinkIcon />, 
            type: MenuItemType.Button, 
            hide: !props.shortcutsEnabled || 
                props.shortcutsList.length === 0 ||
                props.shortcutsList.flatMap(sgts => sgts.shortcuts).length === 0,
            buttonItemSelected: props.expandShortcutsList
        },
        { label: MenuItemLabel.EMPTY, type: MenuItemType.Divider},
        { label: MenuItemLabel.PROFILE, icon: <AccountCircleOutlinedIcon />, type: MenuItemType.Link, url: "~/me" },
        { label: MenuItemLabel.EMPTY, type: MenuItemType.Divider },
        { label: MenuItemLabel.ADMIN_PORTAL, type: MenuItemType.Link, icon: <BuildIcon />, pushToBrowserHistory: true, url: "~/admin/dashboard", userRole: UserRoleStrings.AUTHOR },
        { label: MenuItemLabel.MANAGE_MY_CONTENT, type: MenuItemType.Link, icon: <PostAddIcon />, pushToBrowserHistory: true, url: "~/admin/posts", userRole: UserRoleStrings.CONTRIBUTOR, strictRole: true },
    ]);

    return items;
}

const SignInMenuItem = (): IMenuItem => {
    return { label: MenuItemLabel.SIGN_IN, icon: <AccountCircleOutlinedIcon />, type: MenuItemType.Link, url: "~/" };
}

const Menu: React.FunctionComponent<PropsWithRedux> = props => {
    const [expandedMenuItem, setExpandedMenuItem] = useState("");
    const theme = useTheme();
    const isSmallAndSmaller = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

    const classes = useStyles({
        drawerExpanded: props.expand, 
        drawerWidth: props.drawerWidth,
        expandedDrawerWidth: props.expandedDrawerWidth
    });

    return (
        <Drawer
            open
            variant="permanent"
            PaperProps={{
                elevation: 5
            }}
            classes={{
                paper: props.drawerPaperClassName
            }}
        >
            <List className={classes.list}>
                {!props.publicAccess
                    ? MenuItems(props).map((menuItem, index) => {
                        switch (menuItem.type) {
                            case MenuItemType.Divider:
                                return <Divider key={index} light />;
                            case MenuItemType.Link:
                                const isLinkSelected = new MenuUtils(props.tenant, props.locationPathName)
                                    .isLinkItemSelected(menuItem);
                                return (
                                    <MenuItem
                                        key={index}
                                        expand={props.expand}
                                        expandedMenuItem={expandedMenuItem}
                                        menuItem={menuItem}
                                        selected={isLinkSelected}
                                        toggleExpand={props.toggleExpand}
                                        setExpandedMenuItem={setExpandedMenuItem}
                                        hideIcons={isSmallAndSmaller && !props.expand}
                                    />
                                );
                            case MenuItemType.Button:
                                const isButtonSelected = menuItem.buttonItemSelected ?? false;
                                return (
                                    <MenuItem
                                        key={index}
                                        expand={props.expand}
                                        expandedMenuItem={expandedMenuItem}
                                        menuItem={menuItem}
                                        selected={isButtonSelected}
                                        toggleExpand={props.toggleExpand}
                                        setExpandedMenuItem={setExpandedMenuItem}
                                        onMenuItemClicked={props.onMenuItemClicked}
                                        hideIcons={isSmallAndSmaller && !props.expand}
                                    />
                                );
                            default:
                                return <></>;
                        }
                    })
                    : <MenuItem
                        key={0}
                        expand={props.expand}
                        expandedMenuItem={expandedMenuItem}
                        menuItem={SignInMenuItem()}
                        toggleExpand={props.toggleExpand}
                        setExpandedMenuItem={setExpandedMenuItem}
                        hideIcons={true}
                    />
                }
            </List>
            <div className={`${classes.poweredBy} ${props.expand ? "expanded" : ""}`}>
                <div>Powered by Sparrow Connected</div>
                <div>{process.env.REACT_APP_ADMINPORTAL_VERSION}</div>
            </div>
        </Drawer>
    );
}

interface ComponentProps {
    expand: boolean;
    locationPathName: string;
    publicAccess?: boolean;
    toggleExpand: () => void;
    tenant: string;
    drawerPaperClassName: string;
    hideIcons?: boolean;
    drawerWidth: number;
    expandedDrawerWidth: number;
    shortcutsList: ShortcutGroupToShortcuts[];
    expandShortcutsList: boolean;
    onMenuItemClicked?: (menuItem: IMenuItem) => void;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
        documentsEnabled: state.settings.clientSettings.documentsEnabled,
        eventsEnabled: state.settings.clientSettings.mobileEventsEnabled,
        navigation: state.portalPages.navigation,
        portalPagesEnabled: state.settings.clientSettings.portalPagesEnabled,
        currentUser: state.settings.currentUser,
        shortcutsEnabled: state.settings.clientSettings.shortcutsEnabled
    })
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(Menu);
